import { filterClasses } from "@swivl-mono/helpers";
import { ReactNode } from "react";

import { WidgetSwitch } from "../../Widgets/WidgetSwitch";

interface PolicyToggleRowProps {
  label: string;
  description?: string;
  /** True while the worksheet is in edit mode (Details v3 grammar). */
  isEditing: boolean;
  on: boolean;
  /** Inheritance pill (+ Revert while editing), composed by the worksheet. */
  controls: ReactNode;
  /** Revealed by a choice on the row above — indent the label to match. */
  nested?: boolean;
  /** Reveals gated rows beneath it — drop the bottom padding so the group is 8px apart. */
  hasRevealed?: boolean;
  onToggle: (on: boolean) => void;
}

/**
 * Toggle row on the Details v3 Amenities & Services model (CortexFeatureRow):
 * the switch is always rendered and simply disabled outside edit mode, rather
 * than swapping to plain text — WidgetSwitch carries its own disabled styling,
 * so an inherited or read-only row dims on its own. Control order matches that
 * row too: switch, then the inheritance pill, then Revert. The field
 * description is edit-mode only, matching the Details v3 setting rows.
 */
export function PolicyToggleRow({
  label,
  description,
  isEditing,
  on,
  controls,
  nested = false,
  hasRevealed = false,
  onToggle
}: PolicyToggleRowProps): React.ReactElement {
  const verticalPadding = nested ? "pt-2" : hasRevealed ? "pt-4" : "py-4";
  return (
    // Same grid as the other policy rows (Details v3's Facility Info layout), so a
    // switch lines up with the inputs and dropdowns above and below it. The narrow
    // layout is a container query on the row's own width, matching CortexRowShell —
    // see the comment there for why it is not a viewport breakpoint.
    <div className="@container">
      <div
        className={filterClasses(
          "grid grid-cols-[280px_minmax(240px,1fr)_max-content] items-center gap-x-4 @max-[780px]:grid-cols-[200px_minmax(240px,1fr)]",
          verticalPadding
        )}
      >
        {/* A nested row keeps the label column empty and stacks its label above the
          switch, lining the child block up with the control it belongs to. */}
        {nested ? <span /> : <div className="body-sm-alt text-grey-900">{label}</div>}
        <div className="min-w-0">
          {nested && <div className="body-sm-alt text-grey-900">{label}</div>}
          {/* Flex, not block: the switch is inline-level, so a block wrapper would give
            it a text line box taller than the control and knock it off the row's
            centre line. */}
          <span className={filterClasses("flex items-center", nested && "mt-1")}>
            <WidgetSwitch on={on} disabled={!isEditing} onSet={onToggle} ariaLabel={label} />
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2 @max-[780px]:col-start-2 @max-[780px]:row-start-2 @max-[780px]:mt-2 @max-[780px]:justify-self-start">
          {controls}
        </div>
        {isEditing && description && <p className="body-sm col-start-2 mt-1 text-grey-600">{description}</p>}
      </div>
    </div>
  );
}
